import { supabase } from '../lib/supabase';

// Address Analysis Service - moved before investigationService to fix forward reference
export const addressService = {
  // Get address details with risk factors
  async getAddressDetails(address, network) {
    try {
      const { data, error } = await supabase?.from('crypto_addresses')?.select(`
          *,
          address_risk_factors(*),
          cluster_addresses(
            cluster:address_clusters(id, name, description, category)
          )
        `)?.eq('address', address)?.eq('network', network)?.single();

      if (error && error?.code !== 'PGRST116') { // Not found is ok
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Create or update address
  async createOrUpdateAddress(addressData) {
    try {
      const { data, error } = await supabase?.from('crypto_addresses')?.upsert([{
          address: addressData?.address,
          network: addressData?.network,
          category: addressData?.category || 'unknown',
          risk_score: addressData?.risk_score || 0,
          balance: addressData?.balance || 0,
          transaction_count: addressData?.transaction_count || 0,
          first_activity_at: addressData?.first_activity_at,
          last_activity_at: addressData?.last_activity_at,
          updated_at: new Date()?.toISOString()
        }], {
          onConflict: 'address,network'
        })?.select()?.single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Flag address as suspicious
  async flagAddress(addressId, notes = '') {
    try {
      const { data, error } = await supabase?.from('crypto_addresses')?.update({
          is_flagged: true,
          flagged_at: new Date()?.toISOString(),
          notes
        })?.eq('id', addressId)?.select()?.single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Add risk factor to address
  async addRiskFactor(addressId, riskFactorData) {
    try {
      const { data, error } = await supabase?.from('address_risk_factors')?.insert([{
          address_id: addressId,
          title: riskFactorData?.title,
          description: riskFactorData?.description,
          severity: riskFactorData?.severity,
          impact_score: riskFactorData?.impact_score || 0,
          confidence: riskFactorData?.confidence || 0,
          source: riskFactorData?.source
        }])?.select()?.single();

      if (error) {
        throw error;
      }

      // Recalculate address risk score
      await this.updateAddressRiskScore(addressId);

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Update address risk score
  async updateAddressRiskScore(addressId) {
    try {
      const { data: newScore } = await supabase?.rpc('calculate_address_risk_score', { address_uuid: addressId });

      if (newScore !== null) {
        await supabase?.from('crypto_addresses')?.update({ risk_score: newScore })?.eq('id', addressId);
      }

      return { data: newScore, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Search addresses
  async searchAddresses(query, filters = {}) {
    try {
      let queryBuilder = supabase?.from('crypto_addresses')?.select(`
          *,
          address_risk_factors(count)
        `);

      if (query) {
        queryBuilder = queryBuilder?.ilike('address', `%${query}%`);
      }

      if (filters?.network) {
        queryBuilder = queryBuilder?.eq('network', filters?.network);
      }

      if (filters?.category) {
        queryBuilder = queryBuilder?.eq('category', filters?.category);
      }

      if (filters?.min_risk_score) {
        queryBuilder = queryBuilder?.gte('risk_score', filters?.min_risk_score);
      }

      if (filters?.is_flagged !== undefined) {
        queryBuilder = queryBuilder?.eq('is_flagged', filters?.is_flagged);
      }

      const { data, error } = await queryBuilder?.order('risk_score', { ascending: false })?.limit(50);

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }
};

// Investigation Management Service
export const investigationService = {
  // Get all investigations with optional filtering
  async getInvestigations(filters = {}) {
    try {
      let query = supabase?.from('investigations')?.select(`
          *,
          investigator:user_profiles(id, full_name, email),
          investigation_addresses(
            id,
            address:crypto_addresses(id, address, network, risk_score, category)
          )
        `);

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }
      
      if (filters?.investigator_id) {
        query = query?.eq('investigator_id', filters?.investigator_id);
      }

      const { data, error } = await query?.order('created_at', { ascending: false });
      
      if (error) {
        throw error;
      }
      
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get single investigation with full details
  async getInvestigationById(id) {
    try {
      const { data, error } = await supabase?.from('investigations')?.select(`
          *,
          investigator:user_profiles(id, full_name, email, role),
          investigation_addresses(
            id,
            is_primary,
            notes,
            address:crypto_addresses(
              id, address, network, category, risk_score, 
              balance, transaction_count, first_activity_at, last_activity_at
            )
          ),
          forensic_reports(id, title, status, created_at),
          investigation_activities(
            id, activity_type, description, created_at,
            user:user_profiles(full_name)
          )
        `)?.eq('id', id)?.single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Create new investigation
  async createInvestigation(investigationData) {
    try {
      const { data, error } = await supabase?.from('investigations')?.insert([{
          case_number: investigationData?.case_number,
          title: investigationData?.title,
          description: investigationData?.description,
          investigator_id: investigationData?.investigator_id,
          priority: investigationData?.priority || 1,
          target_addresses: investigationData?.target_addresses || []
        }])?.select()?.single();

      if (error) {
        throw error;
      }

      // Log activity
      await this.logActivity(data?.id, 'CASE_OPENED', 'Investigation case opened', {
        case_number: data?.case_number,
        priority: data?.priority
      });

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Update investigation
  async updateInvestigation(id, updates) {
    try {
      const { data, error } = await supabase?.from('investigations')?.update({
          ...updates,
          updated_at: new Date()?.toISOString()
        })?.eq('id', id)?.select()?.single();

      if (error) {
        throw error;
      }

      // Log activity
      await this.logActivity(id, 'CASE_UPDATED', 'Investigation updated', updates);

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Close investigation
  async closeInvestigation(id, summary) {
    try {
      const { data, error } = await supabase?.from('investigations')?.update({
          status: 'completed',
          closed_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        })?.eq('id', id)?.select()?.single();

      if (error) {
        throw error;
      }

      // Log activity
      await this.logActivity(id, 'CASE_CLOSED', 'Investigation completed', { summary });

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Add address to investigation
  async addAddressToInvestigation(investigationId, addressData) {
    try {
      // First, ensure address exists or create it
      let addressId = addressData?.address_id;
      
      if (!addressId && addressData?.address) {
        const addressResult = await addressService?.createOrUpdateAddress({
          address: addressData?.address,
          network: addressData?.network,
          category: addressData?.category || 'unknown'
        });
        
        if (addressResult?.error) {
          throw addressResult?.error;
        }
        
        addressId = addressResult?.data?.id;
      }

      // Link address to investigation
      const { data, error } = await supabase?.from('investigation_addresses')?.insert([{
          investigation_id: investigationId,
          address_id: addressId,
          is_primary: addressData?.is_primary || false,
          notes: addressData?.notes
        }])?.select(`
          *,
          address:crypto_addresses(id, address, network, risk_score, category)
        `)?.single();

      if (error) {
        throw error;
      }

      // Log activity
      await this.logActivity(investigationId, 'ADDRESS_ADDED', 'Address added to investigation', {
        address: addressData?.address,
        network: addressData?.network
      });

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Log investigation activity
  async logActivity(investigationId, activityType, description, metadata = {}) {
    try {
      const { data, error } = await supabase?.from('investigation_activities')?.insert([{
          investigation_id: investigationId,
          activity_type: activityType,
          description,
          metadata
        }]);

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get investigation activities
  async getInvestigationActivities(investigationId, limit = 50) {
    try {
      const { data, error } = await supabase?.from('investigation_activities')?.select(`
          *,
          user:user_profiles(id, full_name, email)
        `)?.eq('investigation_id', investigationId)?.order('created_at', { ascending: false })?.limit(limit);

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get dashboard metrics
  async getDashboardMetrics() {
    try {
      const [
        totalInvestigationsResult,
        activeInvestigationsResult,
        completedInvestigationsResult,
        flaggedAddressesResult
      ] = await Promise.all([
        supabase?.from('investigations')?.select('*', { count: 'exact', head: true }),
        supabase?.from('investigations')?.select('*', { count: 'exact', head: true })?.eq('status', 'active'),
        supabase?.from('investigations')?.select('*', { count: 'exact', head: true })?.eq('status', 'completed'),
        supabase?.from('crypto_addresses')?.select('*', { count: 'exact', head: true })?.eq('is_flagged', true)
      ]);

      const totalInvestigations = totalInvestigationsResult?.count || 0;
      const activeInvestigations = activeInvestigationsResult?.count || 0;
      const completedInvestigations = completedInvestigationsResult?.count || 0;
      const flaggedAddresses = flaggedAddressesResult?.count || 0;

      const { data: fundsTraced } = await supabase?.from('investigations')?.select('total_funds_traced');

      const totalFundsTraced = fundsTraced?.reduce((sum, inv) => sum + (parseFloat(inv?.total_funds_traced) || 0), 0) || 0;

      return {
        data: {
          totalInvestigations: totalInvestigations || 0,
          activeInvestigations: activeInvestigations || 0,
          completedInvestigations: completedInvestigations || 0,
          flaggedAddresses: flaggedAddresses || 0,
          totalFundsTraced: totalFundsTraced?.toFixed(2)
        },
        error: null
      };
    } catch (error) {
      return { data: null, error };
    }
  }
};

// Transaction Service
export const transactionService = {
  // Get transactions for an address
  async getAddressTransactions(address, network, limit = 100) {
    try {
      const { data, error } = await supabase?.from('crypto_transactions')?.select('*')?.or(`from_address.eq.${address},to_address.eq.${address}`)?.eq('network', network)?.order('timestamp', { ascending: false })?.limit(limit);

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Create or update transaction
  async createOrUpdateTransaction(transactionData) {
    try {
      const { data, error } = await supabase?.from('crypto_transactions')?.upsert([{
          tx_hash: transactionData?.tx_hash,
          network: transactionData?.network,
          from_address: transactionData?.from_address,
          to_address: transactionData?.to_address,
          amount: transactionData?.amount,
          usd_value: transactionData?.usd_value,
          type: transactionData?.type,
          risk_score: transactionData?.risk_score || 0,
          confirmations: transactionData?.confirmations || 0,
          block_height: transactionData?.block_height,
          gas_price: transactionData?.gas_price,
          gas_used: transactionData?.gas_used,
          transaction_fee: transactionData?.transaction_fee,
          timestamp: transactionData?.timestamp
        }], {
          onConflict: 'tx_hash,network'
        })?.select()?.single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Analyze transaction patterns
  async analyzeTransaction(investigationId, transactionId, analysisType, findings, riskIndicators) {
    try {
      const { data, error } = await supabase?.from('transaction_analysis')?.insert([{
          investigation_id: investigationId,
          transaction_id: transactionId,
          analysis_type: analysisType,
          findings,
          risk_indicators: riskIndicators
        }])?.select()?.single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get transaction analysis for investigation
  async getTransactionAnalysis(investigationId) {
    try {
      const { data, error } = await supabase?.from('transaction_analysis')?.select(`
          *,
          transaction:crypto_transactions(*),
          analyst:user_profiles(id, full_name)
        `)?.eq('investigation_id', investigationId)?.order('analyzed_at', { ascending: false });

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }
};

// Report Service
export const reportService = {
  // Get reports for investigation
  async getReports(investigationId) {
    try {
      const { data, error } = await supabase?.from('forensic_reports')?.select(`
          *,
          author:user_profiles!author_id(id, full_name),
          reviewer:user_profiles!reviewed_by(id, full_name)
        `)?.eq('investigation_id', investigationId)?.order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Create report
  async createReport(reportData) {
    try {
      const { data, error } = await supabase?.from('forensic_reports')?.insert([{
          investigation_id: reportData?.investigation_id,
          title: reportData?.title,
          report_type: reportData?.report_type,
          content: reportData?.content,
          summary: reportData?.summary
        }])?.select()?.single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Update report
  async updateReport(reportId, updates) {
    try {
      const { data, error } = await supabase?.from('forensic_reports')?.update({
          ...updates,
          updated_at: new Date()?.toISOString()
        })?.eq('id', reportId)?.select()?.single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Publish report
  async publishReport(reportId) {
    try {
      const { data, error } = await supabase?.from('forensic_reports')?.update({
          status: 'published',
          published_at: new Date()?.toISOString()
        })?.eq('id', reportId)?.select()?.single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }
};

// Template Service
export const templateService = {
  // Get investigation templates
  async getTemplates() {
    try {
      const { data, error } = await supabase?.from('investigation_templates')?.select(`
          *,
          creator:user_profiles(id, full_name)
        `)?.eq('is_public', true)?.order('usage_count', { ascending: false });

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Use template (increment usage count)
  async useTemplate(templateId) {
    try {
      const { data, error } = await supabase?.rpc('increment_template_usage', { template_id: templateId });

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  }
};

export default {
  investigation: investigationService,
  address: addressService,
  transaction: transactionService,
  report: reportService,
  template: templateService
};